// Module: rejectReason.js
// Purpose: Shared helper — maps raw backend reject_reason codes to human-readable labels.
//          Used by ManualOrdersPanel and SmartOrderForm notification path.

//--> Matches defensively (lowercase substring). Fallback: surface the broker's own error text
//*   when present, else the raw code (never blank).
//*  Source codes: risk-gate ("risk_gate:circuit_band", "risk_gate:price_band_vs_ltp"); broker-forwarded
//*  ("broker_reject:BROKER_REJECT_PERMANENT:{...json...}"); legacy watchdog ("watchdog_broker_silent:").

//! issue 10509 D30-8 — the canonical RejectClass tokens (backend/shared/schemas/reject_class.py). Used
//   ONLY to recognise WHERE the class sits in a `broker_reject:` wrapper, never to relabel it.
const REJECT_CLASS_TOKENS = new Set([
  'VALIDATION', 'BROKER_REJECT_PERMANENT', 'BROKER_REJECT_TRANSIENT', 'EXCHANGE_REJECT',
  'NETWORK_TIMEOUT', 'NETWORK_PRE_SEND', 'NETWORK_POST_SEND', 'RATE_LIMIT_THROTTLE',
  'MARGIN_INSUFFICIENT', 'REASON_NOT_PROVIDED_BY_BROKER',
]);

//--> Human wording for the fixed adapter sub_reason slugs, used ONLY when a row carries no broker
//    text (the sync shape `broker_reject:{CLASS}:{sub}`). Unlisted slugs are humanized.
const SUB_REASON_LABELS = {
  price_tick_grid: "price is not on the contract's tick grid",
  circuit_band: 'price outside the exchange circuit band',
};

//--> A sub_reason slug: lowercase snake_case (e.g. price_tick_grid, margin_shortfall) or the one
//    fixed spaced literal the adapters emit ("Invalid Algo ID").
const SUB_REASON_SLUG = /^(?:[a-z][a-z0-9_]*|Invalid Algo ID)$/;

//! issue 10509 D30-8 — parse every persisted `broker_reject:` plain-text shape into its parts:
//   async classed : broker_reject:{BROKER}:{CLASS}:{sub_reason}:{broker text}   (10509 F3(c))
//   async classed, sub carries the text : broker_reject:{BROKER}:{CLASS}:margin_shortfall:{text}
//   async legacy  : broker_reject:{BROKER}:{broker text}
//   sync          : broker_reject:{CLASS}:{sub_reason or broker text}
//   Returns {broker, rejectClass, subReason, text} (any may be null) or null when not a wrapper.
//   Exported so a surface that has a badge/tooltip can show class + sub_reason beside the text.
export function parseBrokerReject(raw) {
  if (!raw) return null;
  const str = String(raw);
  const m = str.match(/^\s*broker_reject:([\s\S]*)$/);
  if (!m) return null;
  const parts = m[1].split(':');
  let broker = null;
  let rejectClass = null;
  let rest;
  if (REJECT_CLASS_TOKENS.has(parts[0])) {
    rejectClass = parts[0];
    rest = parts.slice(1);
  } else if (parts.length > 1 && REJECT_CLASS_TOKENS.has(parts[1])) {
    broker = parts[0];
    rejectClass = parts[1];
    rest = parts.slice(2);
  } else {
    broker = parts.length > 1 ? parts[0] : null;
    rest = parts.length > 1 ? parts.slice(1) : parts;
    const text = rest.join(':').trim();
    return { broker, rejectClass: null, subReason: null, text: text || null };
  }
  let subReason = null;
  if (rest.length && SUB_REASON_SLUG.test(rest[0].trim())) {
    subReason = rest[0].trim();
    rest = rest.slice(1);
  }
  const text = rest.join(':').trim();
  return { broker, rejectClass, subReason, text: text || null };
}

//! Extract the broker's OWN message from a "broker_reject:..." wrapper.
//   The payload is EITHER JSON (Breeze {"Error":...}/{"Message":...}, Flattrade
//   {"stat":"Not_Ok","emsg":...}, per broker-api-contracts/flattrade.md:88) OR plain text. Try JSON
//   first (forward-compatible with either wire), then the plain-text shapes via parseBrokerReject:
//   the broker text only — never the internal CLASS / sub_reason tokens (D30-8). A row with no broker
//   text falls back to the sub_reason's human wording.
function extractBrokerMessage(raw) {
  const braceIdx = raw.indexOf('{');
  if (braceIdx !== -1) {
    try {
      const obj = JSON.parse(raw.slice(braceIdx));
      const msg =
        obj.Error || obj.error || obj.Message || obj.message || obj.emsg || obj.rejreason || null;
      if (msg) return msg;
    } catch {
      // not valid JSON — fall through to the plain-text extraction below
    }
  }
  const parsed = parseBrokerReject(raw);
  if (!parsed) return null;
  if (parsed.text) return parsed.text;
  if (parsed.subReason) return SUB_REASON_LABELS[parsed.subReason] ?? humanizeToken(parsed.subReason);
  return null;
}

//--> #104 R4.4/§8.3 — data-driven label maps for the new `failure_type`/`origin` columns
//    (order_placement_rejections, R2.8/R9). Kept HERE, not as a new local humanizer inside
//    MonitoringTab/SignalEvaluationPanel, per this file's own single-source convention.
const FAILURE_TYPE_LABELS = {
  protective_leg_failure_naked: 'Protective leg failed — position left naked',
};

const ORIGIN_LABELS = {
  internal: 'Internal',
  broker: 'Broker',
};

//! #104 §8.3 — the R2.7 historical marker: a pre-migration row has NO source data for these
//   new columns. Never render blank or silently fall back to a live-looking mode like 'paper'.
export const HISTORICAL_INCOMPLETE_LABEL = 'historical / predates field';

//--> Snake_case -> sentence case fallback, same humanization rule as rejectReasonLabel's own
//    unmapped-token branch (:82-89) — an unrecognised future failure_type/origin still reads
//    human instead of disappearing (feedback_silent_failure_antipattern).
function humanizeToken(raw) {
  const s = String(raw);
  if (/^[a-z][a-z0-9_:]*$/.test(s)) {
    const words = s.replace(/[_:]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s;
}

export function failureTypeLabel(raw) {
  if (!raw) return null;
  return FAILURE_TYPE_LABELS[raw] ?? humanizeToken(raw);
}

export function originLabel(raw) {
  if (!raw) return null;
  return ORIGIN_LABELS[raw] ?? humanizeToken(raw);
}

//--> #118 — refusal reasons produced by the ONE entry-admission authority
//    (`order_service.check_entry_admission` -> `EntryAdmissionRefusal.reason`, String(50)) plus
//    the infrastructure refusals promoted through `record_entry_refusal`. Each label says WHAT
//    happened and, where the user can act, WHAT TO DO.
//!   The retired ambiguity-veto reason is deliberately ABSENT: #118 deleted that veto (#114), so
//!   no new row can carry it. Historical rows fall through to the humanizer, which is correct —
//!   inventing a present-tense label for a rule that no longer exists would be worse than a
//!   plain one. Its literal is not spelled anywhere here; the R6.1 delete guard greps for it.
const ADMISSION_REJECT_LABELS = {
  //--> Lifecycle / sequencing (business decisions — the user can act on these).
  lifecycle_open:
    'Not placed — this strategy and account already have an entry open. Close it before entering again.',
  multi_stage_blocked:
    'Not placed — an earlier entry stage has not filled yet. Later stages wait for the one before them.',
  //--> Session boundary (Q5).
  cutoff_stand_down:
    'Not placed — past the broker square-off cutoff for this exchange and product. Intraday entries stop before the broker closes positions.',
  //--> Fail-closed identity. Not user-actionable; say so plainly rather than implying it is.
  lifecycle_identity_missing:
    'Not placed — this position could not be matched to a tracked trade. Support has been alerted; do not retry.',
  //--> Infrastructure — RETRYABLE, and the copy must say so, or a user re-sends blind.
  admission_barrier_unavailable:
    'Not placed — the safety check store was briefly unavailable. Nothing was sent to the broker; retry shortly.',
  dedup_redis_error:
    'Not placed — the duplicate-order check was unavailable. Nothing was sent to the broker; retry shortly.',
  md_health_read_error:
    'Not placed — market-data health could not be confirmed. Nothing was sent to the broker; retry shortly.',
  order_throttled:
    'Not placed — order rate limit reached. Retry shortly.',
};

//--> 144-2 R3 — the ONE entry-outcome reason TAXONOMY (backend/shared/schemas/entry_outcome_reasons.py):
//    runner skips (decision lane), OBSERVED paper-entry TTL refusals (OMS), derived session causes
//    (read model) and the historical decode-only `pubsub_loss_suspected`. Matched EXACTLY (===) like
//    ADMISSION_REJECT_LABELS. Each label names the condition actually observed — never a guessed cause.
//!   `pubsub_loss_suspected` is LEGACY: no writer emits it any more, but historical rows still
//!   decode, and its label must say the cause was never observed.
const ENTRY_OUTCOME_REASON_LABELS = {
  //--> Runner-side skips (strategy-worker decision lane).
  cold_gate_not_warm:
    'Entry skipped — the strategy was still warming up (not enough bars yet).',
  model_held_bar_superseded:
    'Entry skipped — the bar was held waiting for model data and a newer bar replaced it.',
  model_context_late:
    'Entry skipped — the model input for this bar arrived too late.',
  //--> Observed paper-entry TTL refusals (OMS).
  ttl_expired_no_qualifying_tick:
    'Paper entry expired — the price feed was live but never reached the entry level in time.',
  ttl_expired_feed_stale:
    'Paper entry expired — the price feed was stale when it expired.',
  ttl_expired_feed_unobserved:
    'Paper entry expired — feed health could not be read at expiry, so the cause is unknown.',
  pubsub_loss_suspected:
    'Paper entry expired (legacy; cause not observed).',
  //--> Derived session-coverage causes (read model; never written by a service).
  feed_stale: 'Price feed was stale during the session.',
  worker_absent: 'Strategy worker was not running.',
  runner_skips_observed: 'Some bars were skipped by the strategy runner.',
  entry_refusals_observed: 'Some entries were refused.',
  decision_log_skipped: 'The bar was evaluated but its decision record could not be stored.',
  stale_evidence: 'The strategy runner has not evaluated a recent bar.',
};

//! 10509-12 D89 — the OMS `order_placement_rejections.reject_reason` column is width-limited
//   (D89-B); a reason wider than the column is trimmed and the untruncated value moved into
//   `sub_reason` as "<full reason>" or "<full reason> | <existing sub_reason>". Without this,
//   rejectReasonLabel() only ever saw the trimmed reject_reason and could garble the PRIMARY
//   label (e.g. "Wire Bound Leg Missing Li"). Prefer the backend-computed `reject_reason_full`
//   field (rejection_writer.full_reject_reason, same logic, one authority) when present; fall
//   back to the same reconstruction here for older payloads that predate that field so they
//   keep labelling correctly without a redeploy-in-lockstep requirement.
export function fullRejectReason(row) {
  if (!row) return null;
  if (row.reject_reason_full) return row.reject_reason_full;
  const { reject_reason: reason, sub_reason: subReason } = row || {};
  if (!reason || !subReason) return reason ?? null;
  const firstSegment = String(subReason).split(' | ')[0];
  if (firstSegment.startsWith(reason) && firstSegment.length > reason.length) {
    return firstSegment;
  }
  return reason;
}

export function rejectReasonLabel(raw) {
  if (!raw) return null;
  const r = String(raw).toLowerCase();

  //--> Local risk-gate rejections (pre-broker).
  if (r.includes('exchange_reject') && r.includes('circuit')) return 'Broker rejected: outside exchange circuit band';
  if (r.includes('circuit_band')) return 'Rejected: price outside circuit band';
  if (r.includes('price_band_vs_ltp')) return 'Rejected: price too far from market (fat-finger guard)';

  //! ICICI's "Limit exceed: API call per minute" is a MISLEADING message — it is NOT a real
  //   rate-limit (our usage sits far under the broker's 100/min cap). Empirically it is returned
  //   when order placement is attempted OUTSIDE market hours ("Try after some time" = during
  //   market hours). Surface the honest cause instead of a fake rate-limit. (Matches whether the
  //   token is at the top level or inside a broker_reject JSON, since `r` is the full raw string.)
  if (r.includes('limit exceed') && r.includes('per minute')) {
    return 'Broker rejected — the market may be closed. Please try during market hours.';
  }

  //! Wrong-URL 404 from a stale endpoint (should not occur post-fix; surfaced clearly if it does).
  if (r.includes('no http resource') && r.includes('order')) {
    return 'Broker rejected: order endpoint unavailable (please report this).';
  }

  //--> Legacy watchdog fabricated-silence reject (pre-fix orders — no broker error existed).
  if (r.includes('watchdog_broker_silent')) {
    return 'Rejected: broker status could not be confirmed (legacy).';
  }

  //--> #102 R16/R21 — OCO-cancel reasons stamped onto a CANCELLED order's reject_reason
  //    (order_projector.py:3180 sibling-cancel path; oco_manager.py:55 explicit-cancel path).
  //!   `oco_cancelled_by:<uuid>` must NOT leak the raw UUID onto the user's screen — match the
  //!   prefix only and never interpolate the id into the label.
  if (r.includes('oco_sibling_filled')) {
    return 'Cancelled — the paired bracket order (SL/TP) filled first';
  }
  if (r.includes('oco_cancelled_by')) {
    return 'Cancelled — superseded by a filled order';
  }

  //--> #118 — the unified admission authority's own refusal vocabulary. Matched EXACTLY (===),
  //    not by substring, because these are short internal enums and a substring rule would let
  //    an unrelated future reason collide with one silently.
  //!   Without these each one falls through to the generic humanizer and reads as a bare enum
  //!   ('Lifecycle open', 'Cutoff stand down') — technically not blank, but it tells a user
  //!   nothing about what happened or what to do, which on a refused real-money order is the
  //!   difference between an explanation and a shrug.
  if (ADMISSION_REJECT_LABELS[r]) return ADMISSION_REJECT_LABELS[r];
  if (Object.prototype.hasOwnProperty.call(ENTRY_OUTCOME_REASON_LABELS, r)) return ENTRY_OUTCOME_REASON_LABELS[r];

  //--> Broker-forwarded reject: surface the broker's OWN message, not the internal wrapper.
  if (r.includes('broker_reject')) {
    const brokerMsg = extractBrokerMessage(String(raw));
    if (brokerMsg) return `Broker rejected: ${brokerMsg}`;
    return 'Broker rejected the order.';
  }

  //--> #104 R8.9 — an UNMAPPED reason must still read human, never a raw enum-shaped code.
  //    A snake_case/colon code (e.g. 'price_band_violation') is humanized to sentence case
  //    ('Price band violation'); already-human strings (broker text, sentences) pass through.
  const s = String(raw);
  if (/^[a-z][a-z0-9_:]*$/.test(s)) {
    const words = s.replace(/[_:]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return s; // already human-ish — never blank
}
